import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import ActivitiesModal from './ActivitiesModal';
import './MobileMenu.css';

const MobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  // Вертикальный центр кнопки-крестика: по нему выравнивается селектор языка
  const [closeButtonCenter, setCloseButtonCenter] = useState(null);
  const hamburgerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();

  // Закрываем меню при изменении маршрута
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Блокируем скролл при открытом меню
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Селектор языка ставим точно по центральной линии крестика,
  // высота шапки может отличаться от страницы к странице
  useEffect(() => {
    const measureCloseButton = () => {
      if (!hamburgerRef.current) return;
      const rect = hamburgerRef.current.getBoundingClientRect();
      setCloseButtonCenter(rect.top + rect.height / 2);
    };

    measureCloseButton();
    window.addEventListener('resize', measureCloseButton);

    return () => window.removeEventListener('resize', measureCloseButton);
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  // Мои активности открываются модалкой - как на странице профиля
  const openActivities = () => {
    setIsOpen(false);
    setShowActivitiesModal(true);
  };

  // Не показывать меню на страницах входа и регистрации, если пользователь не авторизован
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
  if (isAuthPage && !user) {
    return null;
  }

  return (
    <>
      <button
        ref={hamburgerRef}
        className={`hamburger ${isOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        aria-label={t('a11y.toggleMenu')}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Селектор языка вынесен в строку шапки - на один ряд с крестиком закрытия,
          по горизонтали он остается на левом краю содержимого меню */}
      <div
        className={`mobile-menu-header ${isOpen ? 'active' : ''}`}
        style={closeButtonCenter !== null
          ? { top: `${closeButtonCenter}px`, height: 'auto', transform: 'translateY(-50%)' }
          : undefined}
      >
        <LanguageSwitcher />
      </div>

      <div className={`mobile-menu-overlay ${isOpen ? 'active' : ''}`} onClick={toggleMenu}></div>

      <nav className={`mobile-menu ${isOpen ? 'active' : ''}`}>
        <div className="mobile-menu-content">
          <div className="mobile-menu-top-spacer" aria-hidden="true"></div>

          {!user?.deactivated && (
            <div className="mobile-menu-nav">
              {user && (
                <button
                  onClick={openActivities}
                  className="mobile-nav-button"
                >
                  {t('profile.myActivities')}
                </button>
              )}
              {user && (
                <button
                  onClick={() => handleNavigation('/workouts')}
                  className="mobile-nav-button"
                >
                  {t('workouts.title')}
                </button>
              )}
              <button
                onClick={() => handleNavigation('/rmc')}
                className="mobile-nav-button mobile-nav-button--rmc"
              >
                {t('rmc.title')}
              </button>
              {user && (
                <>
                  <button
                    onClick={() => handleNavigation('/events')}
                    className="mobile-nav-button"
                  >
                    {t('events.title')}
                  </button>
                  <button
                    onClick={() => handleNavigation('/teams')}
                    className="mobile-nav-button"
                  >
                    {t('teams.title')}
                  </button>
                  <button
                    onClick={() => handleNavigation('/users')}
                    className="mobile-nav-button"
                  >
                    {t('users.title')}
                  </button>
                  <button
                    onClick={() => handleNavigation('/results')}
                    className="mobile-nav-button"
                  >
                    {t('results.title')}
                  </button>
                  <button
                    onClick={() => handleNavigation('/ratings')}
                    className="mobile-nav-button"
                  >
                    {t('ratings.title')}
                  </button>
                  <button
                    onClick={() => handleNavigation('/suggestions')}
                    className="mobile-nav-button"
                  >
                    {t('suggestions.navTitle')}
                  </button>
                  {/* На мобильных новости открываются страницей, без модалки */}
                  <button
                    onClick={() => handleNavigation('/news')}
                    className="mobile-nav-button"
                  >
                    {t('news.title')}
                  </button>
                  {user.analytics_access && (
                    <button
                      onClick={() => handleNavigation('/admin/analytics')}
                      className="mobile-nav-button"
                    >
                      {t('admin.analytics.navTitle')}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          <div className="mobile-menu-footer">
            <div className="mobile-footer-text">
              {t('footer.madeWithLove')} <span className="footer-heart">♥</span> {t('footer.team')}
            </div>
            <div className="mobile-footer-links">
              <a href="mailto:d.obedkin@cyclingvirtualleague.com" className="mobile-footer-link">
                {t('footer.feedback')}
              </a>
              <span className="mobile-footer-separator">|</span>
              <a href="https://t.me/rouvy_cvl" target="_blank" rel="noopener noreferrer" className="mobile-footer-link">
                {t('footer.telegram')}
              </a>
              <span className="mobile-footer-separator">|</span>
              <Link to="/terms" className="mobile-footer-link">
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {user && (
        <ActivitiesModal
          isOpen={showActivitiesModal}
          onClose={() => setShowActivitiesModal(false)}
          userId={user.id}
        />
      )}
    </>
  );
};

export default MobileMenu;